import { Fragment, forwardRef } from 'react';

import {
  LinearProgress,
  TableCell,
  TableCellProps,
  Typography,
  alpha,
  withStyles,
} from '@material-ui/core';

import { isSameDay } from 'date-fns';
import * as duration from 'duration-fns';

import {
  formatDateFuture,
  formatDuration,
} from '@spotify/confidence-core-react';
import {
  RolloutDataScheduleScheduleStepsStatus,
  RolloutDataScheduleState,
  RolloutListItemFragment,
  WorkflowV1WorkflowInstanceCheckState,
} from '@spotify/confidence-graphql';
import { usePlanningDates } from '@spotify/plugin-confidence-workflows-module-planning';

export const Bullet = () => <Fragment> &bull; </Fragment>;

interface ProgressCellProps extends TableCellProps {
  workflowInstance: Pick<
    RolloutListItemFragment,
    'checks' | 'moduleData2' | 'state'
  >;
}

export const ProgressCell = forwardRef<HTMLTableCellElement, ProgressCellProps>(
  ({ workflowInstance, ...props }, ref) => {
    const { state } = workflowInstance;

    return (
      <TableCell {...props} ref={ref}>
        {state === 'draft' && (
          <DraftProgress workflowInstance={workflowInstance} />
        )}
        {state === 'live' && (
          <LiveProgress workflowInstance={workflowInstance} />
        )}
        {(state === 'ended' || state === 'archived') && (
          <EndedProgress workflowInstance={workflowInstance} />
        )}
      </TableCell>
    );
  },
);

function DraftProgress({
  workflowInstance,
}: {
  workflowInstance: Pick<
    RolloutListItemFragment,
    'checks' | 'moduleData2' | 'state'
  >;
}) {
  const { plannedStartDate } = usePlanningDates(
    workflowInstance.moduleData2.planning,
  );

  const complete = !workflowInstance.checks.some(
    c => c.state === WorkflowV1WorkflowInstanceCheckState.Failed,
  );

  return (
    <Fragment>
      {complete ? 'Ready' : 'A few things left'}

      {plannedStartDate && complete && (
        <Typography variant="inherit" color="textSecondary" component="span">
          <Bullet />
          {formatDateFuture(plannedStartDate)}
        </Typography>
      )}
    </Fragment>
  );
}

function EndedProgress({
  workflowInstance,
}: {
  workflowInstance: Pick<RolloutListItemFragment, 'moduleData2'>;
}) {
  const { actualRuntime } = usePlanningDates(
    workflowInstance.moduleData2.planning,
  );

  return (
    <Fragment>
      Finished
      {actualRuntime && (
        <Typography variant="inherit" color="textSecondary" component="span">
          <Bullet />
          {formatDuration(actualRuntime)}
        </Typography>
      )}
    </Fragment>
  );
}

const StyledLinearProgress = withStyles(theme => ({
  root: {
    // Reserve some space to the right so that it looks more aligned with the text
    // in the cell to the right.
    marginRight: theme.spacing(3),
    // Some space between the progress bar and the text, without wrappinge
    // everything in a flex container.
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
  },
  colorSecondary: {
    backgroundColor:
      theme.palette.type === 'light'
        ? alpha(theme.palette.secondary.main, 0.1)
        : alpha(theme.palette.secondary.main, 0.3),
  },
}))(LinearProgress);

const formatNumber = (number: number) => {
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(number);
};

function LiveProgress({
  workflowInstance,
}: {
  workflowInstance: Pick<RolloutListItemFragment, 'moduleData2'>;
}) {
  const { actualStartDate, currentRuntime } = usePlanningDates(
    workflowInstance.moduleData2.planning,
  );

  const exposure = workflowInstance.moduleData2?.exposure?.exposure?.reduce(
    (acc, curr) => acc + (Number(curr.totalExposureCount) || 0),
    0,
  );

  const isRampingUp =
    workflowInstance.moduleData2?.rollout?.schedule?.state ===
    RolloutDataScheduleState.Enabled;

  const nextIndex =
    workflowInstance.moduleData2?.rollout?.schedule?.steps.findIndex(
      step => step.status !== RolloutDataScheduleScheduleStepsStatus.Succeeded,
    );

  const prevStep =
    nextIndex !== undefined && nextIndex > 0 - 1
      ? workflowInstance.moduleData2?.rollout?.schedule?.steps[nextIndex - 1]
      : null;

  const prevReach =
    prevStep?.status === RolloutDataScheduleScheduleStepsStatus.Succeeded
      ? Number(prevStep.reach) * 100
      : null;

  const nextStep =
    nextIndex !== undefined && nextIndex !== -1
      ? workflowInstance.moduleData2?.rollout?.schedule?.steps[nextIndex]
      : null;

  const nextReach =
    nextStep?.status === RolloutDataScheduleScheduleStepsStatus.Pending
      ? Number(nextStep.reach) * 100
      : null;

  const steps =
    nextStep &&
    nextIndex !== undefined &&
    workflowInstance.moduleData2?.rollout?.schedule
      ? workflowInstance.moduleData2?.rollout?.schedule?.steps.slice(
          0,
          nextIndex + 1,
        )
      : [];

  const scheduleRuntime = steps.reduce(
    (acc, step) =>
      duration.sum(acc, duration.parse(step.durationSincePreviousStep)),
    {},
  );

  const nextDate = actualStartDate
    ? duration.apply(actualStartDate, scheduleRuntime)
    : null;

  const progress = workflowInstance.moduleData2?.rollout?.reach
    ? Number(workflowInstance.moduleData2?.rollout?.reach) * 100
    : null;

  // If there a schedule, it has failed, and we're still at the same reach
  const isAutomaticallyFailed =
    workflowInstance.moduleData2?.rollout?.schedule?.state ===
      RolloutDataScheduleState.Failed &&
    progress !== null &&
    progress === prevReach;

  const renderText = () => {
    if (
      nextDate &&
      nextReach &&
      isRampingUp &&
      isSameDay(nextDate, new Date())
    ) {
      return (
        <Fragment>
          <Bullet />
          {/* example: 80% at 10:22 */}
          {/* example: 80% tomorrow */}
          {nextReach}% {formatDateFuture(nextDate)}
        </Fragment>
      );
    }

    if (isAutomaticallyFailed) {
      return (
        <Fragment>
          <Bullet />
          Schedule stopped
        </Fragment>
      );
    }

    if (exposure !== undefined) {
      if (exposure === 0) {
        return (
          <Fragment>
            <Bullet />
            None exposed yet
          </Fragment>
        );
      }

      return (
        <Fragment>
          <Bullet />
          {formatNumber(exposure)} exposed
        </Fragment>
      );
    }

    return null;
  };

  return (
    <Fragment>
      {progress !== null && (
        <Fragment>
          <StyledLinearProgress
            variant="determinate"
            value={progress}
            color="secondary"
          />
          <Typography
            variant="caption"
            color="textSecondary"
            component="div"
            noWrap
          >
            {progress}%{renderText()}
          </Typography>
        </Fragment>
      )}

      {progress === null && (
        <Typography variant="inherit">
          {formatDuration(currentRuntime)}
        </Typography>
      )}
    </Fragment>
  );
}
